/**
 * TeamsView - Operational teams (equipes) dashboard
 *
 * Displays teams as cards with member counts, type badges, and color indicators.
 * Supports filtering by status and type, team creation, and comparison.
 */
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Users, Search, RefreshCw, Plus, ArrowLeftRight, ChevronRight, MapPin, Car, Wrench, User } from 'lucide-react';
import { teamAPI } from '../../api';
import TeamDetailModal from './TeamDetailModal.jsx';
import { useSearch } from '../../v3/context/SearchContext.jsx';
import SkeletonLoader from '../common/SkeletonLoader.jsx';
import TeamFormModal from './TeamFormModal.jsx';
import TeamComparisonView from './TeamComparisonView.jsx';

// Les libellés viennent de i18n (teams.type.* / teams.status.*) : ici, seules les classes CSS.
const TEAM_TYPE_CONFIG = {
  chantier: { bgClass: 'bg-orange-100', textClass: 'text-orange-800' },
  maintenance: { bgClass: 'bg-blue-100', textClass: 'text-blue-800' },
  transport: { bgClass: 'bg-green-100', textClass: 'text-green-800' },
  mixed: { bgClass: 'bg-purple-100', textClass: 'text-purple-800' },
  other: { bgClass: 'bg-gray-100', textClass: 'text-gray-800' }
};

const STATUS_CONFIG = {
  active: { dotClass: 'bg-green-500' },
  inactive: { dotClass: 'bg-yellow-500' },
  archived: { dotClass: 'bg-gray-400' }
};

export default function TeamsView() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { searchQuery: search } = useSearch();
  const [statusFilter, setStatusFilter] = useState('active');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);

  const { data: teamsData, isLoading, error, refetch } = useQuery({
    queryKey: ['teams', statusFilter, typeFilter],
    queryFn: () => {
      const params = { _limit: 100 };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.team_type = typeFilter;
      return teamAPI.getAll(params);
    },
    staleTime: 30000,
  });

  const teams = useMemo(() => {
    const list = teamsData?.data || [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(item =>
      item.name?.toLowerCase().includes(q) ||
      item.code?.toLowerCase().includes(q) ||
      item.description?.toLowerCase().includes(q)
    );
  }, [teamsData, search]);

  const handleCompareToggle = (teamId) => {
    setSelectedForCompare(prev => {
      if (prev.includes(teamId)) return prev.filter(id => id !== teamId);
      if (prev.length >= 4) return prev; // Max 4 teams for comparison
      return [...prev, teamId];
    });
  };

  const handleCreated = () => {
    setShowCreateForm(false);
    queryClient.invalidateQueries({ queryKey: ['teams'] });
  };

  if (showComparison && selectedForCompare.length >= 2) {
    return (
      <TeamComparisonView
        teamIds={selectedForCompare}
        onBack={() => setShowComparison(false)}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Users className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{t('teams.list.title')}</h1>
              <p className="text-sm text-gray-500">{t('teams.list.count', { count: teams.length })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedForCompare.length >= 2 && (
              <button
                onClick={() => setShowComparison(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 text-sm font-medium"
              >
                <ArrowLeftRight className="h-4 w-4" />
                {t('teams.list.compare', { count: selectedForCompare.length })}
              </button>
            )}
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
            >
              <Plus className="h-4 w-4" />
              {t('teams.list.create')}
            </button>
            <button
              onClick={() => refetch()}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title={t('teams.list.refreshTitle')}
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          {/* Status pills */}
          <div className="flex gap-1">
            {['', 'active', 'inactive', 'archived'].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {s ? t(`teams.status.${s}`) : t('teams.list.allStatuses')}
              </button>
            ))}
          </div>
          {/* Type pills */}
          <div className="flex gap-1">
            {['', 'chantier', 'maintenance', 'transport', 'mixed'].map(ty => (
              <button
                key={ty}
                onClick={() => setTypeFilter(ty)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  typeFilter === ty
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {ty ? t(`teams.type.${ty}`) : t('teams.list.allTypes')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Loading / Error */}
      {isLoading && (
        <SkeletonLoader type="cards" cards={6} columns={3} />
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {t('teams.list.loadError', { message: error.message })}
        </div>
      )}

      {/* Team Grid */}
      {!isLoading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map(team => {
            const typeConfig = TEAM_TYPE_CONFIG[team.team_type] || TEAM_TYPE_CONFIG.other;
            const statusConfig = STATUS_CONFIG[team.status] || STATUS_CONFIG.active;
            const isSelected = selectedForCompare.includes(team.id);
            const counts = team.member_counts || {};

            return (
              <div
                key={team.id}
                className={`bg-white rounded-lg shadow-sm border-2 transition-all hover:shadow-md cursor-pointer ${
                  isSelected ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-transparent'
                }`}
              >
                {/* Color bar */}
                <div
                  className="h-1.5 rounded-t-lg"
                  style={{ backgroundColor: team.color || '#3B82F6' }}
                />

                <div className="p-4" onClick={() => setSelectedTeamId(team.id)}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 truncate">{team.name}</h3>
                        <span className={`w-2 h-2 rounded-full ${statusConfig.dotClass}`} title={t(`teams.status.${team.status}`, { defaultValue: team.status })} />
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{team.code}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeConfig.bgClass} ${typeConfig.textClass}`}>
                      {t(`teams.type.${team.team_type}`, { defaultValue: team.team_type })}
                    </span>
                  </div>

                  {team.description && (
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{team.description}</p>
                  )}

                  {/* Member counts */}
                  <div className="mt-3 flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1" title={t('teams.list.driversTooltip')}>
                      <User className="h-3.5 w-3.5" />
                      {counts.drivers || 0}
                    </span>
                    <span className="flex items-center gap-1" title={t('teams.list.vehiclesTooltip')}>
                      <Car className="h-3.5 w-3.5" />
                      {counts.vehicles || 0}
                    </span>
                    <span className="flex items-center gap-1" title={t('teams.list.toolsTooltip')}>
                      <Wrench className="h-3.5 w-3.5" />
                      {counts.tools || 0}
                    </span>
                  </div>

                  {/* Site badge */}
                  {team.working_site_id && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                      <MapPin className="h-3 w-3" />
                      {t('teams.list.siteBadge', { id: team.working_site_id })}
                    </div>
                  )}
                </div>

                {/* Footer: compare checkbox */}
                <div
                  className="px-4 py-2 border-t border-gray-100 flex items-center justify-between"
                  onClick={(e) => e.stopPropagation()}
                >
                  <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleCompareToggle(team.id)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    {t('teams.list.compareCheckbox')}
                  </label>
                  <button
                    onClick={() => setSelectedTeamId(team.id)}
                    className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5"
                  >
                    {t('teams.list.detailLink')} <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
              </div>
            );
          })}

          {teams.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              <Users className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="font-medium">{t('teams.list.emptyTitle')}</p>
              <p className="text-sm mt-1">{t('teams.list.emptyHint')}</p>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {selectedTeamId && (
        <TeamDetailModal
          teamId={selectedTeamId}
          onClose={() => setSelectedTeamId(null)}
          onUpdated={() => queryClient.invalidateQueries({ queryKey: ['teams'] })}
        />
      )}

      {/* Create Form Modal */}
      {showCreateForm && (
        <TeamFormModal
          onClose={() => setShowCreateForm(false)}
          onSaved={handleCreated}
        />
      )}
    </div>
  );
}
